import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { GridRowModes } from "@mui/x-data-grid";

import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";

import {
  successToast,
  failureToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";

import {
  createCommissionSlabRow,
  updateCommissionSlabRow,
} from "./helper";

import { regex } from "views/pages/manage/common/constant";
import { updateRequest } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { gridStyles } from "views/pages/manage/edpOps/issuanceIDcard/printIdCardAndMapRfid/constant";
import EnhanceDataGrid from "views/pages/manage/common/data-grid/EnhanceDataGrid";


const createEmptyRow = () => ({
  id: uuidv4(),
  stream: "",
  studentsFrom: "",
  studentsTo: "",
  career: "",
  courseAttribute: "",
  courseAttributeValue: "",
  percentage: "",
  isNew: true,
});

const CommissionSlabTable = forwardRef(
  (
    {
      rows,
      setRows,
      currentPage,
      onPaginationChange,
      careerOptions = [],
      courseAttributeOptions = [],
      courseAttributeValueMap = [],
      academicGroupOptions = [],
      slabId,
      currPageSize,
      totalRecords,
      userPermissions,
      loadSlabDetails
    },
    ref
  ) => {
    const [editRowId, setEditRowId] = useState(null);
    const [rowForm, setRowForm] = useState({});
    const [savingRowId, setSavingRowId] = useState(null);
    const [rowModesModel, setRowModesModel] = useState({});


    const isRowValid = useMemo(() => {
      const {
        stream,
        studentsFrom,
        studentsTo,
        career,
        courseAttribute,
        courseAttributeValue,
        percentage,
      } = rowForm;

      return (
        stream &&
        studentsFrom !== "" &&
        studentsTo !== "" &&
        career &&
        courseAttribute &&
        courseAttributeValue &&
        percentage !== ""
      );
    }, [rowForm]);


    const resetEditState = useCallback((rowId) => {
      setEditRowId(null);
      setRowForm({});
      setRowModesModel((prev) => ({
        ...prev,
        [rowId]: {
          mode: GridRowModes.View,
          ignoreModifications: true,
        },
      }));
    }, []);


    const handleAddRow = useCallback(() => {
      if (editRowId) {
        failureToast("Please save or cancel current row first");
        return;
      }

      const newRow = createEmptyRow();
      setRows((prev) => [newRow, ...prev]);

      setEditRowId(newRow.id);
      setRowForm(newRow);

      setRowModesModel((prev) => ({
        ...prev,
        [newRow.id]: {
          mode: GridRowModes.Edit,
          fieldToFocus: "studentsFrom",
        },
      }));
    }, [editRowId, setRows]);

    useImperativeHandle(ref, () => ({
      addRow: handleAddRow,
    }));


    const handleEdit = useCallback(
      (row) => {
        if (editRowId) {
          failureToast("Please save or cancel current row first");
          return;
        }

        setEditRowId(row.id);
        setRowForm(row);

        setRowModesModel((prev) => ({
          ...prev,
          [row.id]: {
            mode: GridRowModes.Edit,
            fieldToFocus: "studentsFrom",
          },
        }));
      },
      [editRowId]
    );

    const handleSave = async (rowId) => {
      if (!isRowValid) {
        failureToast("Please fill all required fields");
        return;
      }

      if (!slabId) {
        failureToast("Slab ID is required");
        return;
      }

      setSavingRowId(rowId);

      try {
        const payload = updateRequest({
          masterId: Number(slabId),
          stream: rowForm.stream,
          career: rowForm.career,
          courseAttribute: rowForm.courseAttribute,
          courseAttributeValue: rowForm.courseAttributeValue,
          studentsFrom: Number(rowForm.studentsFrom),
          studentsTo: Number(rowForm.studentsTo),
          percentage: Number(rowForm.percentage),
        });

        let updatedRow;

        if (rowForm.isNew) {
          const response = await createCommissionSlabRow(payload);

          if(response?.code==200){
            await loadSlabDetails()
          }

          

          successToast("Row created successfully");
        } else {
          const res = await updateCommissionSlabRow(rowForm.id, payload);
          if(res?.code==200){
            await loadSlabDetails()
            successToast(res?.message || "Row updated successfully");
          }
        }


        resetEditState(rowId);
      } catch (error) {
        console.error("Save failed:", error);

        failureToast("Failed to save row");
        resetEditState(rowId);
      } finally {
        setSavingRowId(null);
      }
    };


    const handleCancel = (rowId) => {
      if (rows.find((r) => r.id === rowId)?.isNew) {
        setRows((prev) =>
          prev.filter((r) => r.id !== rowId)
        );
      }

      resetEditState(rowId);
    };


    const handleFieldChange = (key, value) => {
      setRowForm((prev) => ({
        ...prev,
        [key]: value,
      }));
    };


    const columns = useMemo(
      () => [
        new AGridColDef("stream", "Stream")
          .setMinWidth(120)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <div className="w-100">
              <AAutoComplete
                items={academicGroupOptions}
                currentValue={
                  academicGroupOptions.find(
                    (c) => c.label === rowForm.stream
                  ) || null
                }
                handleChange={(val) =>
                  handleFieldChange("stream", val?.label || "")
                }
                isMulti={false}
                height="36px"
              />
            </div>
          )),

        new AGridColDef("studentsFrom", "Students From")
          .setMinWidth(110)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <AInput
              type="number"
              value={rowForm.studentsFrom}
              handler={(v) =>
                handleFieldChange("studentsFrom", v)
              }
              regex={regex.numeric}
            />
          )),

        new AGridColDef("studentsTo", "Students To")
          .setMinWidth(110)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <AInput
              type="number"
              value={rowForm.studentsTo}
              handler={(v) =>
                handleFieldChange("studentsTo", v)
              }
              regex={regex.numeric}
            />
          )),

        new AGridColDef("career", "Career")
          .setMinWidth(130)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <div className="w-100">
              <AAutoComplete
                items={careerOptions}
                currentValue={
                  careerOptions.find(
                    (c) => c.label === rowForm.career
                  ) || null
                }
                handleChange={(val) =>
                  handleFieldChange("career", val?.label || "")
                }
                isMulti={false}
                height="36px"
              />
            </div>
          )),

        new AGridColDef("courseAttribute", "Course Attribute")
          .setMinWidth(150)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <div className="w-100">
              <AAutoComplete
                items={courseAttributeOptions}
                currentValue={
                  courseAttributeOptions.find(
                    (c) => c.label === rowForm.courseAttribute
                  ) || null
                }
                handleChange={(val) =>
                  handleFieldChange(
                    "courseAttribute",
                    val?.label || ""
                  )
                }
                isMulti={false}
                height="36px"
              />
            </div>
          )),

        new AGridColDef(
          "courseAttributeValue",
          "Course Attribute Value"
        )
          .setMinWidth(170)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <div className="w-100">
              <AAutoComplete
                items={courseAttributeValueMap}
                currentValue={
                  courseAttributeValueMap.find(
                    (c) =>
                      c.label ===
                      rowForm.courseAttributeValue
                  ) || null
                }
                handleChange={(val) =>
                  handleFieldChange(
                    "courseAttributeValue",
                    val?.label || ""
                  )
                }
                isMulti={false}
                height="36px"
              />
            </div>
          )),

        new AGridColDef("percentage", "Percentage")
          .setMinWidth(100)
          .editable(true)
          .renderCellComponent((params) => params.value ?? "-")
          .renderEditCellComponent(() => (
            <AInput
              type="text"
              value={rowForm.percentage}
              handler={(v) =>
                handleFieldChange("percentage", v)
              }
              regex={regex.percentage}
            />
          )),

        new AGridColDef("actions", "Actions", false)
          .setMinWidth(130)
          .pinColumnLast(true)
          .renderCellComponent((params) => {
            const isEditing =
              editRowId === params.row.id;

            if (isEditing) {
              return (
                <>
                  <button
                    className="btn btn-success btn-sm mr-2"
                    onClick={() =>
                      handleSave(params.row.id)
                    }
                    disabled={
                      !isRowValid ||
                      savingRowId === params.row.id
                    }
                  >
                    Save
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                      handleCancel(params.row.id)
                    }
                    disabled={
                      savingRowId === params.row.id
                    }
                  >
                    Cancel
                  </button>
                </>
              );
            }

            return (
              <button
                className="btn btn-primary btn-sm"
                onClick={() =>
                  handleEdit(params.row)
                }
                disabled={!!editRowId || !(userPermissions||[]).includes("U") }
              >
                Edit
              </button>
            );
          }),
      ],
      [
        rowForm,
        editRowId,
        savingRowId,
        isRowValid,
        academicGroupOptions,
        careerOptions,
        courseAttributeOptions,
        courseAttributeValueMap,
      ]
    );



    return (
   
      <EnhanceDataGrid
        removeWrapperContainer
        rowModesModel={rowModesModel}
        paginationModel={{ page: currentPage, pageSize: currPageSize }}
        rows={rows || []}
        columns={columns}
        rowId={(row) => row.id}
        rowHeight={50}
        loading={false}
        rowCount={totalRecords}
        nextButtonHandler={(pageNo, pageSize) => onPaginationChange({ page : pageNo, pageSize })}
        currentPage={currentPage}
        sx={gridStyles}
        autoHeight
        pinLastColumn
      />
    );
  }
);

export default CommissionSlabTable;
